import { useCallback, useState, type ReactElement } from 'react';
import { t, type MapsLinkAnswer } from '@bms2/shared';
import type { PlaceOption } from '../../components/modals/EventModal';
import { PAGES_PATH, apiFetch } from './api';
import { SiteModal } from './SiteModal';
import { readMapsLinkAnswer } from './MapsLinkPaste';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * Add New on the event modal's Place type-ahead (decision 0348): the footer
 * opens the + Add Site combo modal of piece 13, stacked over the event, and
 * the Site it creates is picked as the place. 🔨 The combo modal is reused as
 * it stands — the company chosen inside it, the building picked or typed —
 * and nothing about a place is created from free text.
 */
export interface AddPlaceHost {
  /** Handed to the event modal's `onAddNewPlace`; a pasted link's answer fills Add Site's step one (decision 1600). */
  onAddNewPlace: (accept: (place: PlaceOption) => void, fromLink?: MapsLinkAnswer) => void;
  /** Handed to the event modal's `readMapsLink`: the API's read of a pasted Maps link (decision 1600). */
  readMapsLink: (link: string) => Promise<MapsLinkAnswer | null>;
  /** The stacked + Add Site modal, rendered beside the event modal. */
  siteModal: ReactElement;
}

/** The new Site as the place list knows it, read back from the calendar's own places so its zone and its company come with it. */
export async function placeForSite(siteId: string, apiBaseUrl: string): Promise<PlaceOption> {
  try {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/calendar/places`);
    if (response.ok) {
      const body = (await response.json()) as { rows: PlaceOption[] };
      const found = body.rows.find((row) => row.id === siteId && row.kind === 'site');
      if (found) return found;
    }
  } catch (error) {
    reportCaughtInBrowser('records.addPlace.list', error);
    // The list read failing leaves the place named by its id alone; the event still saves against the Site.
  }
  return { id: siteId, label: t('event.newSite'), kind: 'site', timeZone: null };
}

export function useAddPlace(apiBaseUrl: string, openerId: string | null = null): AddPlaceHost {
  const [accept, setAccept] = useState<((place: PlaceOption) => void) | null>(null);
  const [fromLink, setFromLink] = useState<MapsLinkAnswer | null>(null);
  const onAddNewPlace = useCallback((handler: (place: PlaceOption) => void, answer?: MapsLinkAnswer) => {
    setFromLink(answer ?? null);
    setAccept(() => handler);
  }, []);
  const close = useCallback(() => setAccept(null), []);
  const readMapsLink = useCallback((link: string) => readMapsLinkAnswer(link, apiBaseUrl), [apiBaseUrl]);
  const siteModal = (
    <SiteModal
      key={accept ? `open:${fromLink?.link ?? ''}` : 'closed'}
      open={accept !== null}
      fromLink={fromLink}
      apiBaseUrl={apiBaseUrl}
      openerId={openerId}
      onCancel={close}
      onCreated={({ siteId }) => {
        const handler = accept;
        if (!handler) return;
        void placeForSite(siteId, apiBaseUrl).then((place) => handler(place));
      }}
    />
  );
  return { onAddNewPlace, readMapsLink, siteModal };
}
